import React, { useState, useEffect } from "react";
import { Box, useTheme, Button, Switch, Tooltip } from "@mui/material";
import Header from "components/Header";
import { Edit, LockReset } from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import FlexBetween from "../../components/FlexBetween";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import DataGridCustomToolbar from "components/DataGridCustomToolbar";
import { deleteAdminUser, getAdminUser } from "service/users/AdminUsers";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  AdminUsersModal,
  AdminChangePasswordModal,
} from "components/admin-users/AdminUsersModal";
import ShowFullScreenBackdrop from "components/ShowFullScreenBackdrop";
import { useSelector, useDispatch } from "react-redux";
import {
  setAdminPage,
  setAdminPageSize,
  setAdminTotalItems,
} from "redux/paginationSlice";

const AdminUsers = () => {
  const theme = useTheme();
  const [adminUserData, setAdminUserData] = useState([]);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [adminUserEdit, setAdminUserEdit] = useState({});
  const [newPasswordId, setNewPasswordId] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const { page, pageSize, totalItems } = useSelector(
    (state) => state.pagination.adminUsersPagination
  );
  const dispatch = useDispatch();

  const handleDialogOpen = () => {
    setAddDialogOpen(true);
  };

  const handlePasswordChange = (newPasswordId) => {
    setDialogOpen(true);
    setNewPasswordId(newPasswordId);
  };

  const handleDialogClose = () => {
    setAddDialogOpen(false);
    setDialogOpen(false);
    setTimeout(() => {
      setAdminUserEdit({});
    }, 500);
  };

  const handleAdminUsersEdit = (userData) => {
    setAddDialogOpen(true);
    setAdminUserEdit(userData);
  };

  const handleAdminUsersIsActiveToggle = async (userId, isActive) => {
    try {
      const confirmed = window.confirm(
        `Are you sure you want to set this user as ${
          isActive === 1 ? "inactive" : "active"
        }?`
      );

      if (!confirmed) {
        return;
      }
      setOpen(true);

      await deleteAdminUser(userId, { isActive: isActive === 1 ? 0 : 1 });
      await fetchAdminUserList();

      toast.success(`User is now ${isActive === 1 ? "inactive" : "active"}.`);
    } catch (error) {
      toast.error("Failed to update user status.");
    } finally {
      setOpen(false);
    }
  };

  const fetchAdminUserList = async () => {
    try {
      setIsLoading(true);
      const response = await getAdminUser({ page, pageSize });
      const startIndex = (page - 1) * pageSize + 1;
      const updatedAdminUserData = response.data.data.map(
        (adminUser, index) => ({
          ...adminUser,
          sno: startIndex + index,
        })
      );
      setAdminUserData(updatedAdminUserData);
      dispatch(setAdminTotalItems(response.data.totalDataAdmin));
      // console.log(updatedAdminUserData);
    } catch (error) {
      console.error("There was a problem fetching the admin user list:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAdminUserList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  const column = [
    {
      field: "sno",
      headerName: "S.NO",
      flex: 0.5,
    },
    {
      field: "fullName",
      headerName: "FULL NAME",
      flex: 1,
    },
    {
      field: "email",
      headerName: "EMAIL",
      flex: 1,
    },
    {
      field: "role",
      headerName: "ROLE",
      flex: 0.5,
      renderCell: (params) => {
        const { value: role } = params;

        if (role === 1) {
          return "Super Admin";
        } else if (role === 2) {
          return "Admin";
        } else if (role === 3) {
          return "Manager";
        } else if (role === 4) {
          return "Staff";
        } else {
          return null;
        }
      },
    },
    {
      field: "isActive",
      headerName: "STATUS",
      flex: 0.6,
      renderCell: (params) => {
        const isActive = params.value === 1;
        return (
          <Switch
            color="secondary"
            checked={isActive}
            onClick={() =>
              handleAdminUsersIsActiveToggle(params.row._id, params.value)
            }
          />
        );
      },
    },
    {
      field: "actions",
      headerName: "ACTIONS",
      flex: 0.5,
      renderCell: (params) => {
        return (
          <>
            <Box sx={{ mr: 2, cursor: "pointer" }}>
              <Tooltip title="Edit" placement="top" arrow>
                <Edit onClick={() => handleAdminUsersEdit(params.row)} />
              </Tooltip>
            </Box>
            <Box sx={{ mr: 2, cursor: "pointer" }}>
              <Tooltip title="Update Password" placement="top" arrow>
                <LockReset
                  onClick={() => handlePasswordChange(params.row._id)}
                  fontSize="large"
                />
              </Tooltip>
            </Box>
          </>
        );
      },
    },
  ];

  return (
    <>
      <ToastContainer />
      <Box m="1.5rem 2.5rem">
        <Header title="ADMIN USERS" subTitle="List of Admin Users" />
        <Box
          mt="40px"
          height="75vh"
          sx={{
            "& .MuiDataGrid-root": {
              border: "none",
            },
            "& .MuiDataGrid-cell": {
              borderBottom: "none",
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: theme.palette.background.alt,
              color: theme.palette.secondary[100],
              borderBottom: "none",
            },
            "& .MuiDataGrid-virtualScroller": {
              backgroundColor: theme.palette.primary.light,
            },
            "& .MuiDataGrid-footerContainer": {
              backgroundColor: theme.palette.background.alt,
              color: theme.palette.secondary[100],
              borderTop: "none",
            },
            "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
              color: `${theme.palette.secondary[200]} !important`,
            },
          }}
        >
          {" "}
          <FlexBetween>
            <FlexBetween></FlexBetween>
            <Button
              sx={{
                mb: "0.5rem",
                width: "8rem",
              }}
              startIcon={<AddCircleIcon />}
              variant="contained"
              color="secondary"
              onClick={handleDialogOpen}
            >
              Add New
            </Button>

            <AdminUsersModal
              open={addDialogOpen}
              handleClose={handleDialogClose}
              handleData={adminUserEdit}
              fetchAdminUserList={fetchAdminUserList}
            />
            <AdminChangePasswordModal
              open={dialogOpen}
              handleClose={handleDialogClose}
              newPasswordId={newPasswordId}
            />
          </FlexBetween>
          <DataGrid
            loading={isLoading || !adminUserData}
            getRowId={(row) => row._id}
            rows={adminUserData || []}
            columns={column}
            components={{ Toolbar: DataGridCustomToolbar }}
            paginationMode="server"
            pagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            pageSize={pageSize}
            rowCount={totalItems}
            onPageChange={(newPage) => dispatch(setAdminPage(newPage + 1))}
            onPageSizeChange={(newPageSize) =>
              dispatch(setAdminPageSize(newPageSize))
            }
            sx={{
              "& .MuiCircularProgress-circle": {
                color: theme.palette.secondary[300],
              },
            }}
          />
        </Box>
      </Box>
      {open && <ShowFullScreenBackdrop open={open} />}
    </>
  );
};

export default AdminUsers;
